import type {
  PhoneBridgeCallState,
  PhoneBridgeCallType,
  PhoneBridgeDialErrorCode,
  ZohoOauthCredentials,
  ZohoPhoneBridgeCallNotifyRequest,
  ZohoPhoneBridgeClickToDialErrorRequest,
} from '@kunzite/shared-types';

import { ApiError, type TokenProvider } from '../services/calls-api';
import { refreshOn401 } from '../services/refresh-helper';

// Re-export legacy aliases for back-compat with F1/F2 callers in this package.
export type CallType = PhoneBridgeCallType;
export type CallState = PhoneBridgeCallState;
export type NotifyCallInput = ZohoPhoneBridgeCallNotifyRequest;
export type NotifyDialErrorInput = ZohoPhoneBridgeClickToDialErrorRequest;
export type DialErrorCode = PhoneBridgeDialErrorCode;

export interface PhoneBridgeClientDeps {
  /** IS base URL (e.g. https://api-zohodesk.kunzite.app). NOT zohoapis.com. */
  baseUrl: string;
  tokenProvider: TokenProvider;
  onJwtUpdate?: (jwt: string, fresh: { expiresAt: string; refreshExpiresAt: string; zohoOauth: ZohoOauthCredentials }) => void;
  fetch?: typeof globalThis.fetch;
}

/**
 * F3.1: PhoneBridge calls now go through the IS proxy. The widget no longer
 * carries the per-tenant OAuth token. Authentication is the standard
 * Authorization: Bearer <jwt> header.
 */
export class PhoneBridgeClient {
  constructor(private readonly deps: PhoneBridgeClientDeps) {}

  async notifyCall(input: NotifyCallInput): Promise<void> {
    await this.send('POST', '/v1/zoho/phonebridge/callnotify', input);
  }

  async notifyDialError(input: NotifyDialErrorInput): Promise<void> {
    await this.send('POST', '/v1/zoho/phonebridge/click-to-dial-error', input);
  }

  private async send(method: 'POST', path: string, body: unknown): Promise<void> {
    const fetcher = this.deps.fetch ?? globalThis.fetch;
    const tok = await this.deps.tokenProvider();
    await refreshOn401<void>(
      {
        baseUrl: this.deps.baseUrl,
        fetch: fetcher,
        ...(this.deps.onJwtUpdate ? { onJwtUpdate: this.deps.onJwtUpdate } : {}),
      },
      (jwt) => fetcher(`${this.deps.baseUrl}${path}`, this.buildInit(method, jwt, body)),
      (res) => this.parseOrThrow(res),
      tok.jwt,
    );
  }

  private buildInit(method: 'POST', jwt: string, body: unknown): RequestInit {
    return {
      method,
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify(body),
    };
  }

  private async parseOrThrow(res: Response): Promise<void> {
    if (res.status >= 200 && res.status < 300) return;
    const text = await res.text();
    const parsed: unknown = text ? safeJsonParse(text) : undefined;
    throw new ApiError(res.status, parsed);
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
